import React from "react";
import ReactGA from "react-ga4";
import { Routes, Route, HashRouter, BrowserRouter } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { SnackbarUtilsConfigurator } from "./utilities/SnackBarUtils";
import { socket } from "./utilities/socket";

import { EventContext } from "./contexts/EventContext";
import Auth from "./pages/Auth/Auth";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Users from "./pages/Users";
import Events from "./pages/Events";
import Calendar from "./pages/Calendar";
import Orders from "./pages/Orders";
import EventController from "./pages/EventController";
import Matchmaking from "./pages/Matchmaking/Matchmaking";
import ProfileQuestions from "./pages/ProfileQuestions";
import Messenger from "./pages/Messenger";
import Admin from "./pages/Admin/Admin";
import Ads from "./pages/Ads/Ads";
import Prompts from "./pages/Prompts/Prompts";
import BlackList from "./pages/BlackList";
import Help from "./pages/Help";
import Reports from "./pages/Reports";
import SystemMessages from "./pages/SystemMessages";
import SystemMessageWatcher from "./components/SystemMessage/SystemMessageWatcher";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import Scanner from "./pages/Scanner";
import Waivers from "./pages/Waivers";
import Notifications from "./pages/Notifications";
import Messaging from "./pages/Messaging";

import AuthContext from "./contexts/AuthContext";

import "./App.css";

ReactGA.initialize(`${process.env.REACT_APP_GOOGLE_ANALYTICS}`);

const theme = createTheme({
  palette: {
    primary: {
      main: "#ef5241",
    },
  },
  typography: {
    fontFamily: ['"Bitter"', "serif"].join(","),
  },
});

const authTheme = createTheme({
  typography: {
    fontFamily: ['"Instrument Sans"', "sans-serif"].join(","),
  },
});

function App() {
  const [loading, setLoading] = React.useState(true);
  const [email, setEmail] = React.useState("");
  const [tempSession, setTempSession] = React.useState(null);
  const [signedIn, setSignedIn] = React.useState(false);
  const [token, setToken] = React.useState(null);
  const [userData, setUserData] = React.useState(null);
  const [pairingEvents, setPairingEvents] = React.useState(null);
  const [pairingEvent, setPairingEvent] = React.useState(null);
  const [attendees, setAttendees] = React.useState([]);
  const [percentages, setPercentages] = React.useState({});
  const [roundInfo, setRoundInfo] = React.useState({});
  const [pairingData, setPairingData] = React.useState(null);
  const [displayData, setDisplayData] = React.useState({
    seconds: 0,
    display: "",
    percentage: 0,
    paused: false,
  });
  const [missingRounds, setMissingRounds] = React.useState([]);
  const [backToBackBreaks, setBackToBackBreaks] = React.useState([]);
  const [notificationCount, setNotificationCount] = React.useState(0);
  const [messageCount, setMessageCount] = React.useState(0);

  React.useEffect(() => {
    try {
      const si = localStorage.getItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.signedIn`
      );
      if (si === "true") {
        setSignedIn(true);
      }
    } catch (e) {
      console.error(`Error with storage: ${e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div></div>;
  }

  return (
    <AuthContext.Provider
      value={{
        email,
        setEmail,
        signedIn,
        setSignedIn,
        token,
        setToken,
        tempSession,
        setTempSession,
      }}
    >
      <SystemMessageWatcher />

      {signedIn ? (
        <BrowserRouter>
          <SnackbarProvider maxSnack={3}>
            <SnackbarUtilsConfigurator />
            <EventContext.Provider
              value={{
                attendees,
                setAttendees,
                percentages,
                setPercentages,
                pairingEvents,
                setPairingEvents,
                pairingEvent,
                setPairingEvent,
                roundInfo,
                setRoundInfo,
                displayData,
                setDisplayData,
                pairingData,
                setPairingData,
                missingRounds,
                setMissingRounds,
                backToBackBreaks,
                setBackToBackBreaks,
                userData,
                setUserData,
                socket,
                notificationCount,
                setNotificationCount,
                messageCount,
                setMessageCount
              }}
            >
              <ThemeProvider theme={theme}>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/calendar" element={<Calendar />} />
                    <Route
                      path="/eventcontroller/:id"
                      element={<EventController />}
                    />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/matchmaking" element={<Matchmaking />} />
                    <Route
                      path="/profilequestions"
                      element={<ProfileQuestions />}
                    />
                    <Route path="/messenger" element={<Messenger />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/blacklist" element={<BlackList />} />
                    <Route path="/ads" element={<Ads />} />
                    <Route path="/prompts" element={<Prompts />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/waivers" element={<Waivers />} />
                    <Route
                      path="/systemmessages"
                      element={<SystemMessages />}
                    />
                    <Route path="/invoices/:orgId" element={<Invoices />} />
                    <Route path="/invoice/:id" element={<InvoiceDetail />} />
                    <Route path="/scanner" element={<Scanner />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/messaging" element={<Messaging />} />
                  </Routes>
                </Layout>
              </ThemeProvider>
            </EventContext.Provider>
          </SnackbarProvider>
        </BrowserRouter>
      ) : (
        <ThemeProvider theme={authTheme}>
          <Auth />
        </ThemeProvider>
      )}
    </AuthContext.Provider>
  );
}

export default App;
